import {Skeleton} from '@material-ui/lab';
import React, {useCallback, useEffect, useState} from 'react';
import {
  Box,
  Button,
  Card,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@material-ui/core';
import moment from 'moment';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import {
  IQuoteSupportingDocumentInput,
  ISupportingDocument,
  ISupportingDocumentInput,
} from 'constants/interfaces';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import {SupportingDocumentForm} from 'components/SupportingDocuments/SupprtingDocumentForm';
import CloseIcon from '@material-ui/icons/Close';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogActions from '@material-ui/core/DialogActions';

export function SupportingDocuments(props: {
  loadSupportingDocuments: () => Promise<ISupportingDocument[]>;
  updateSupportingDocument: (
    document: ISupportingDocumentInput,
  ) => Promise<void>;
  removeSupportingDocument: (documentId: string | number) => Promise<void>;
  readonly: boolean;
  appendOnly?: boolean;
  titleOptions?: string[];
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [documents, setDocuments] = useState<ISupportingDocument[]>([]);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [documentToUpdate, setDocumentToUpdate] =
    useState<ISupportingDocument>();
  const [documentToRemove, setDocumentToRemove] =
    useState<ISupportingDocument>();

  const loadSupportingDocuments = useCallback(() => {
    setIsLoading(true);
    props
      .loadSupportingDocuments()
      .then(setDocuments)
      .catch((error) => {
        alert('Error loading supporting documents. Please try again later');
        console.error(error);
      })
      .finally(() => setIsLoading(false));
  }, [props.loadSupportingDocuments]);

  useEffect(() => {
    loadSupportingDocuments();
  }, [loadSupportingDocuments]);

  const handleCloseUpdateDialog = () => {
    setDocumentToUpdate(undefined);
    setIsUpdateDialogOpen(false);
  };

  const handleUpdateDocument = (document: ISupportingDocumentInput) => {
    setIsUpdating(true);
    props
      .updateSupportingDocument(document)
      .then(() => loadSupportingDocuments())
      .then(handleCloseUpdateDialog)
      .catch((error) => {
        alert('Error updating supporting documents. Please try again later');
        console.error(error);
      })
      .finally(() => setIsUpdating(false));
  };

  const handleRemoveDocument = (document: ISupportingDocument) => {
    setIsUpdating(true);
    props
      .removeSupportingDocument(document.id)
      .then(() => loadSupportingDocuments())
      .then(() => setDocumentToRemove(undefined))
      .catch((error) => {
        alert('Error removing supporting document. Please try again later');
        console.error(error);
      })
      .finally(() => setIsUpdating(false));
  };

  const formatDate = (date: string): string => {
    return moment(parseInt(date)).format('YYYY-MM-DD hh:mm a');
  };

  if (isLoading)
    return <Skeleton variant="rect" height={200} style={{borderRadius: 8}} />;
  if (!documents?.length && props.readonly)
    return (
      <Box padding={1}>
        <Typography variant="body2" color="textSecondary">
          No supporting documents
        </Typography>
      </Box>
    );

  return (
    <>
      <Card className="supporting-documents-card" variant="outlined">
        {!!documents.length && (
          <Box className="row">
            <Box className="date-container">
              <Typography className="bold" variant="body2">
                Date
              </Typography>
            </Box>
            <Box className="title-container">
              <Typography className="bold" variant="body2">
                Title
              </Typography>
            </Box>
            <Box className="row actions-container" />
          </Box>
        )}
        {documents.map((document) => (
          <div key={document.id}>
            <Divider />
            <div className={'row'}>
              <Box className="date-container">
                <Typography variant="body2" color="textSecondary">
                  {formatDate(document.createdDate)}
                </Typography>
              </Box>
              <Box className="title-container">
                <Typography variant="body2" color="textSecondary">
                  {document.title}
                </Typography>
              </Box>
              <Box className="row actions-container">
                <Tooltip title="Open in new tab" aria-label="Open in new tab">
                  <IconButton
                    disabled={false}
                    href={document.url}
                    target="_blank"
                    title="Open Label Document"
                    size="small"
                  >
                    <OpenInNewIcon />
                  </IconButton>
                </Tooltip>
                {!props.appendOnly && (
                  <Tooltip title="Remove document" aria-label="Remove document">
                    <IconButton
                      disabled={props.readonly}
                      size="small"
                      onClick={() => setDocumentToRemove(document)}
                      style={{color: 'red', marginLeft: 8}}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </div>
          </div>
        ))}
        {!props.readonly && (
          <>
            <Divider />
            <Box padding={1} display={'flex'} justifyContent={'center'}>
              <Button
                variant={'text'}
                size={'small'}
                color={'primary'}
                onClick={() => {
                  setIsUpdateDialogOpen(true);
                }}
              >
                Add Supporting Document
              </Button>
            </Box>
          </>
        )}
      </Card>
      <AddOrUpdateSupportingDocumentDialog
        open={isUpdateDialogOpen}
        isLoading={isUpdating}
        document={documentToUpdate}
        onUpdate={handleUpdateDocument}
        onCancel={handleCloseUpdateDialog}
        titleOptions={props.titleOptions}
      />
      <RemoveSupportingDocumentDialog
        open={!!documentToRemove}
        document={documentToRemove}
        onConfirm={() => handleRemoveDocument(documentToRemove!)}
        onCancel={() => setDocumentToRemove(undefined)}
      />
    </>
  );
}

export function AddOrUpdateSupportingDocumentDialog(props: {
  open: boolean;
  isLoading: boolean;
  document?: Partial<ISupportingDocument>;
  onUpdate: (document: IQuoteSupportingDocumentInput) => any;
  onCancel: () => any;
  titleOptions?: string[];
}) {
  const isAdding = !props.document?.id;
  const submitTitle = isAdding ? 'Add' : 'Update';
  return (
    <Dialog open={props.open} onClose={props.onCancel}>
      <DialogTitle>
        <IconButton
          edge="start"
          color="inherit"
          onClick={props.onCancel}
          style={{marginRight: '1rem'}}
          size={'small'}
        >
          <CloseIcon />
        </IconButton>
        {submitTitle} Supporting Document
      </DialogTitle>
      <DialogContent>
        <SupportingDocumentForm
          document={props.document}
          isLoading={props.isLoading}
          submitButtonTitle={submitTitle}
          onSubmit={props.onUpdate}
          titleOptions={props.titleOptions}
          // cancelButtonTitle={'Cancel'}
          // onCancel={props.onCancel}
        />
      </DialogContent>
      <Box mb={2} />
    </Dialog>
  );
}

function RemoveSupportingDocumentDialog(props: {
  open: boolean;
  document?: ISupportingDocument;
  onConfirm: () => any;
  onCancel: () => any;
}) {
  return (
    <Dialog open={props.open} onClose={props.onCancel}>
      <DialogTitle>Confirm Remove Document</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to remove {props.document?.title}? You will not
          be able to undo.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onCancel}>Cancel</Button>
        <Button onClick={props.onConfirm} className="warning-button" autoFocus>
          Remove
        </Button>
      </DialogActions>
    </Dialog>
  );
}
